// UI state: a stack of layers above Home (sheets and full screens), toasts and fatal states.
// No router library: the Telegram BackButton simply pops the top layer.
import { create } from 'zustand';

/** The locked story that triggered the paywall (shown on it); absent when opened from Profile. */
export interface PaywallStory {
  id: string;
  title: string;
  hue: number;
}

export type Layer =
  | { kind: 'player' }
  | { kind: 'sleep' }
  | { kind: 'more' }
  | { kind: 'paywall'; story?: PaywallStory }
  | { kind: 'profile' }
  | { kind: 'language' }
  | { kind: 'welcome' }
  | { kind: 'expired' }
  | {
      kind: 'payment';
      paymentId: string;
      orderNo: number;
      payUrl: string;
      provider: string;
      planCode: string;
      amountUzs: number;
      story?: PaywallStory;
    };

export type PlayerView = 'scene' | 'read' | 'night';

export type LayerKind = Layer['kind'];

export interface Toast {
  id: number;
  text: string;
  tone: 'success' | 'info';
}

interface UiState {
  layers: Layer[];
  /** Overrides everything (initData expired → reopen the app). */
  fatal: 'reopen' | null;
  toast: Toast | null;
  playerView: PlayerView;
  /** Large text in the read view (menu "Text size"); remembered per device. */
  largeText: boolean;
  setPlayerView: (view: PlayerView) => void;
  toggleLargeText: () => void;
  open: (layer: Layer) => void;
  /** Replace the top layer (e.g. paywall → payment). */
  replaceTop: (layer: Layer) => void;
  close: () => void;
  closeKind: (kind: LayerKind) => void;
  closeAll: () => void;
  setFatal: (fatal: 'reopen' | null) => void;
  showToast: (text: string, tone?: Toast['tone']) => void;
  hideToast: () => void;
}

const LARGE_TEXT_KEY = 'fonus-ertak-large-text';
function readLargeText(): boolean {
  try {
    return localStorage.getItem(LARGE_TEXT_KEY) === '1';
  } catch {
    return false;
  }
}

let toastSeq = 0;
let toastTimer: ReturnType<typeof setTimeout> | undefined;

export const useUi = create<UiState>()((set, get) => ({
  layers: [],
  fatal: null,
  toast: null,
  playerView: 'scene',
  largeText: readLargeText(),
  setPlayerView: (playerView) => {
    set({ playerView });
  },
  toggleLargeText: () => {
    const largeText = !get().largeText;
    try {
      localStorage.setItem(LARGE_TEXT_KEY, largeText ? '1' : '0');
    } catch {
      // private mode
    }
    set({ largeText });
  },
  open: (layer) => {
    // A layer kind is never stacked twice (double taps).
    if (get().layers.some((l) => l.kind === layer.kind)) return;
    set((s) => ({ layers: [...s.layers, layer] }));
  },
  replaceTop: (layer) => {
    set((s) => ({ layers: [...s.layers.slice(0, -1), layer] }));
  },
  close: () => {
    set((s) => ({ layers: s.layers.slice(0, -1) }));
  },
  closeKind: (kind) => {
    set((s) => ({ layers: s.layers.filter((l) => l.kind !== kind) }));
  },
  closeAll: () => {
    set({ layers: [] });
  },
  setFatal: (fatal) => {
    set({ fatal, layers: [] });
  },
  showToast: (text, tone = 'info') => {
    clearTimeout(toastTimer);
    set({ toast: { id: ++toastSeq, text, tone } });
    toastTimer = setTimeout(() => {
      get().hideToast();
    }, 2600);
  },
  hideToast: () => {
    set({ toast: null });
  },
}));

export const topLayer = (s: UiState): Layer | undefined => s.layers[s.layers.length - 1];
